import React from 'react';

import { ExtendedPaletteColorName, NeutralColorToken, TextButton, Type } from '@robinhood/design';
import { Info12 } from '@robinhood/design/product/components/FoundationIcon/icons/Info12';

import GoldSubscriptionInfoModal from './GoldSubscriptionInfoModal';
import StudentReferralDisclosure from './StudentReferralDisclosure';
import logger from 'src/clients/LoggingClient';
import { Domain, fetch } from 'src/clients/Network/http/fetch';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import TextInput from 'src/components/creditApplication/TextInput';
import { LayoutStyle, TextStyle } from 'src/constants/styles';
import { GoldGradient } from 'src/constants/theme';
import { useGlobalStore } from 'src/state/global';
import { isValidEmail } from 'src/utils/validations';

const SCHOOL_DOMAIN = 'stanford.edu';

const isValidStanfordEmail = (email: string): boolean => {
  const cleanedEmail = email.trim().toLowerCase();
  if (!isValidEmail(cleanedEmail)) {
    return false;
  }
  const parts = cleanedEmail.split('@');
  if (parts.length !== 2) {
    return false;
  }
  const domain = parts[1];
  return domain === SCHOOL_DOMAIN || domain.endsWith(`.${SCHOOL_DOMAIN}`);
};

const defaultError = 'An unexpected error occurred. Please try again later.';

const StudentVerificationPage = () => {
  const { setCurrentPage } = React.useContext(NavigationContext);
  const [showGoldSubscriptionInfoModal, setShowGoldSubscriptionInfoModal] = React.useState<boolean>(false);
  const [emailError, setEmailError] = React.useState<string>();
  const [email, setEmail] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const buttonState = loading ? 'loading' : !email ? 'disabled' : 'default';

  const onClickInfo = React.useCallback(() => {
    setShowGoldSubscriptionInfoModal(true);
  }, []);

  const onDismissGoldSubscriptionInfoModal = React.useCallback(() => {
    setShowGoldSubscriptionInfoModal(false);
  }, []);

  const onChangeEmail = React.useCallback(({ value }: { value: string }) => {
    setEmailError('');
    setEmail(value);
  }, []);

  const onSignUp = React.useCallback(() => {
    setCurrentPage('LANDING');
  }, [setCurrentPage]);

  const onConfirm = React.useCallback(async () => {
    if (!isValidStanfordEmail(email)) {
      setEmailError('Please enter a valid Stanford email address');
      return;
    }

    useGlobalStore.setState({ personalInformation: { email: email.trim() } });

    try {
      setLoading(true);
      const res = await fetch(Domain.RHCrossSell, '/creditcard/invite/student', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
        }),
      });
      if (res.ok) {
        setCurrentPage('STUDENT_CONFIRMATION');
      } else {
        const data = await res.json();
        setEmailError(data.errorMessage ?? defaultError);
      }
    } catch (error) {
      setEmailError(defaultError);
      logger.error(error, { source: 'StudentVerificationPage.onConfirm' });
    } finally {
      setLoading(false);
    }
  }, [email]);

  return (
    <CreditApplicationPageLayout
      title='Please verify you’re a Stanford student'
      subtitle={
        <div
          css={LayoutStyle.rowAC}
          onClick={onClickInfo}
          role='button'
          tabIndex={0}
          onKeyDown={onClickInfo}
          aria-label='Click to learn more about the free Robinhood Gold membership for Stanford students'
        >
          <Type.Text size='M' color={GoldGradient}>
            {'Get free Gold membership while enrolled.'}
          </Type.Text>
          <div css={[LayoutStyle.ml4, LayoutStyle.mt2]}>
            <Info12 color={ExtendedPaletteColorName.BankingAccentDark} />
          </div>
        </div>
      }
      footer={<StudentReferralDisclosure />}
    >
      <div css={[LayoutStyle.w100, LayoutStyle.mt16, LayoutStyle.flex, LayoutStyle.gap24, TextStyle.textCenter]}>
        <TextInput
          placeholder={`email@${SCHOOL_DOMAIN}`}
          state={!!emailError ? 'error' : 'default'}
          error={emailError}
          value={email}
          onChange={onChangeEmail}
          type='email'
          autoComplete='email'
        />
        <Button label='Confirm email address' width='full' type='button' onClick={onConfirm} state={buttonState} />
        <Type.Text size='S' color={NeutralColorToken.Fg2}>
          {'Not a Stanford student? '}
          <TextButton
            label='Sign up here'
            variant='inline'
            type='button'
            color={NeutralColorToken.Fg2}
            onClick={onSignUp}
          />
        </Type.Text>
      </div>
      {showGoldSubscriptionInfoModal && <GoldSubscriptionInfoModal onDismiss={onDismissGoldSubscriptionInfoModal} />}
    </CreditApplicationPageLayout>
  );
};

export default StudentVerificationPage;
